import { useEffect, useRef, useState } from "react"

import {
  decimalToPercentage,
  percentageToDecimal,
} from "@repo/shared/utils/decimal-precision"

import { Input, type InputProps } from "./input"

/**
 * A number input for editing percentage values that are stored as decimals.
 *
 * Manages its own local string state to avoid the controlled number input
 * issue where a decimal conversion round-trip (decimal -> percentage -> display
 * -> percentage -> decimal) causes the input to "snap" or fail to update.
 *
 * ## When to use
 *
 * Use `PercentageInput` whenever a form field stores a decimal value (e.g. 0.26
 * for 26%) but the user should see and edit a percentage (e.g. 26). This is the
 * case for:
 * - Commission rate fields
 * - Rate / surcharge / discount percentage fields
 * - Any DECIMAL(19,10) field that represents a percentage
 *
 * ## When NOT to use
 *
 * - Fields where the user enters and stores the same unit (no conversion needed)
 * - Monetary amount fields (use `AmountInput`)
 * - Display-only formatted percentages (use `formatRateAsPercentage` instead)
 * - Fields that need a suffix/prefix (use InputGroup with a plain Input)
 *
 * ## Precision
 *
 * The database uses DECIMAL(19,10), which stores 10 decimal places. Because
 * percentage-to-decimal divides by 100, the maximum lossless percentage
 * precision is **8 decimal places** (e.g. 1.45000001 -> 0.0145000001).
 *
 * ## Empty
 *
 * A `null` or `undefined` value renders empty, and clearing the input emits
 * `null` — never `0`, which is a different fact ("0%" rather than "no rate").
 * The same contract as `AmountInput`, so an optional rate can be cleared.
 *
 * @example
 * ```tsx
 * // With TanStack Form
 * <form.Field name="commission_rate">
 *   {(field) => (
 *     <PercentageInput
 *       id={field.name}
 *       value={field.state.value as number | null}
 *       onPercentageChange={(decimal) => field.handleChange(decimal)}
 *       onBlur={field.handleBlur}
 *       min={0}
 *       max={100}
 *     />
 *   )}
 * </form.Field>
 *
 * // Standalone
 * <PercentageInput
 *   value={0.26}
 *   onPercentageChange={(decimal) => setRate(decimal)}
 * />
 * ```
 */
export interface PercentageInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "type"
> {
  /** The decimal value from form/state (e.g. 0.26 for 26%); empty when unset. */
  value: number | null | undefined
  /** Receives the converted decimal, or `null` once the field is cleared. */
  onPercentageChange: (decimalValue: number | null) => void
}

const toDisplay = (decimal: number | null): string =>
  decimal === null ? "" : String(decimalToPercentage(decimal))

export function PercentageInput({
  value,
  onPercentageChange,
  ...inputProps
}: PercentageInputProps) {
  const decimalValue = value ?? null

  const [localValue, setLocalValue] = useState(() => toDisplay(decimalValue))
  const prevDecimalRef = useRef(decimalValue)

  // Sync from external changes (e.g. form reset, undo, initial load)
  // but skip when the change originated from our own onChange.
  useEffect(() => {
    if (prevDecimalRef.current !== decimalValue) {
      prevDecimalRef.current = decimalValue
      setLocalValue(toDisplay(decimalValue))
    }
  }, [decimalValue])

  return (
    <Input
      type="number"
      step="any"
      {...inputProps}
      value={localValue}
      onChange={(e) => {
        const inputValue = e.target.value
        setLocalValue(inputValue)

        if (inputValue === "") {
          prevDecimalRef.current = null
          onPercentageChange(null)
          return
        }

        const numericValue = Number(inputValue)
        if (!Number.isFinite(numericValue)) return

        const converted = percentageToDecimal(numericValue)
        prevDecimalRef.current = converted
        onPercentageChange(converted)
      }}
    />
  )
}
